import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaQuoteLeft, FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getCollection } from '../../firebase/firestore';
import './Testimonials.css';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const { db } = await import('../../firebase/config');

      if (!db) {
        console.warn('Firestore not configured');
        setLoading(false);
        return;
      }

      const data = await getCollection('testimonials');
      // Filter only active testimonials and sort by order
      const activeTestimonials = data
        .filter(t => t.active !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      setTestimonials(activeTestimonials);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-slide every 3 seconds
  useEffect(() => {
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        nextTestimonial();
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [currentIndex, testimonials.length]);

  // Show loading state
  if (loading) {
    return (
      <section className="testimonials-section section">
        <div className="container">
          <div className="section-title" style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h3 className="section-title-gradient">What Our Clients Say</h3>
          </div>
          <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-secondary)' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
            <p style={{ marginTop: '1rem' }}>Loading testimonials...</p>
          </div>
        </div>
      </section>
    );
  }

  // Show empty state if no testimonials
  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section className="testimonials-section section">
      <div className="container">
        <div className="section-title" style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h3 className="section-title-gradient">
            What Our Clients Say
          </h3>
        </div>

        <div className="testimonials-container">
          <button
            className="testimonial-nav prev"
            onClick={prevTestimonial}
            aria-label="Previous testimonial"
          >
            <FaChevronLeft />
          </button>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="testimonial-card"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
            >
              <FaQuoteLeft className="quote-icon" />
              <p className="testimonial-message">{testimonials[currentIndex].message}</p>
              <div className="testimonial-rating">
                {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <div className="testimonial-author">
                <div className="author-image">
                  <img
                    src={testimonials[currentIndex].image}
                    alt={testimonials[currentIndex].name}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/60';
                    }}
                  />
                </div>
                <div className="author-info">
                  <h4>{testimonials[currentIndex].name}</h4>
                  <p>
                    {testimonials[currentIndex].position}
                    {testimonials[currentIndex].position && testimonials[currentIndex].company && ' at '}
                    {testimonials[currentIndex].company}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <button
            className="testimonial-nav next"
            onClick={nextTestimonial}
            aria-label="Next testimonial"
          >
            <FaChevronRight />
          </button>
        </div>

        <div className="testimonial-dots">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
